// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { listThemes, getTheme, saveTheme, deleteTheme, getValueTemplates } from '../api/values';

const newClientId = () => `day_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;

const normalizeDayIndices = (days) => (days || []).map((d, idx) => ({ ...d, index: idx + 1 }));

const SortableDayAccordion = ({
  day,
  idx,
  expanded,
  onToggle,
  dayTypes,
  updateDay,
  removeDay,
  templates,
  editor,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: day.client_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Accordion
        expanded={expanded}
        onChange={onToggle}
        sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  cursor: 'grab',
                  touchAction: 'none',
                  mr: 0.5,
                }}
                aria-label="Sürükleyerek sırala"
                title="Sürükleyerek sırala"
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
              <Chip size="small" label={`${day.index}.`} />
              <Chip
                size="small"
                color={day.type === 'observation' ? 'secondary' : 'default'}
                label={dayTypes.find((d) => d.value === day.type)?.label || day.type}
              />
              <Typography sx={{ fontWeight: 600, flex: 1 }}>{day.title || 'Başlık'}</Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={3}>
              <TextField label="Sıra" type="number" value={day.index} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Başlık"
                value={day.title}
                onChange={(e) => updateDay(idx, { title: e.target.value })}
                fullWidth
                helperText="WhatsApp’ta kalın görünmesi için *başlık* olarak işlenir."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Tür"
                value={day.type}
                onChange={(e) => updateDay(idx, { type: e.target.value })}
                fullWidth
              >
                {dayTypes.map((dt) => (
                  <MenuItem key={dt.value} value={dt.value} disabled={dt.value === 'overview'}>
                    {dt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Mesaj / Açıklama"
                value={day.message}
                onChange={(e) => updateDay(idx, { message: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#111B21' }}>
                WhatsApp Önizleme
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: '#d0c7bd',
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: '#EFEAE2',
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  sx={{
                    fontSize: 14,
                    color: '#111B21',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-line',
                  }}
                  dangerouslySetInnerHTML={{ __html: toHtmlPreview(buildPreview(day, editor.id, templates, editor.title)) }}
                />
              </Box>
            </Grid>
            {day.type === 'observation' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Form ID"
                  value={day.form_id || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  disabled
                  helperText="Otomatik form kimliği"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button color="error" size="small" onClick={() => removeDay(idx)} startIcon={<DeleteIcon />} sx={{ mt: 1 }}>
                Sil
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const defaultForm = (formId = 'form1') => ({
  id: formId,
  description: '',
  notes_label: 'Ek notlar',
  strengths_label: 'Güçlü yönler',
  improvements_label: 'Gelişmesi gerekenler',
  general_label: 'Genel yorum',
  groups: [
    {
      id: 'g1',
      title: 'Gözlem Başlığı',
      emoji: '',
      questions: [
        { id: `${formId}_q1`, text: 'Soru 1' },
        { id: `${formId}_q2`, text: 'Soru 2' },
      ],
    },
  ],
});

const defaultTheme = () => ({
  id: null,
  title: '',
  overview: '',
  days: [
    { client_id: newClientId(), index: 1, title: '1. Gün', message: 'Tanıtım ve açılış', type: 'overview' },
    { client_id: newClientId(), index: 2, title: '2. Gün', message: 'Aile etkinliği', type: 'activity' },
    { client_id: newClientId(), index: 3, title: 'Gözlem', message: '', type: 'observation', form_id: null },
  ],
  form: defaultForm(),
});

const dayTypes = [
  { value: 'overview', label: 'Genel Bakış' },
  { value: 'activity', label: 'Etkinlik' },
  { value: 'observation', label: 'Gözlem / Form' },
];

const defaultTemplates = {
  overview: 'Merhaba {parent_name}\n{overview_message}',
  activity: 'Merhaba {parent_name}\n*{activity_title}*\n{activity_message}',
  observation: 'Merhaba {parent_name}\nForm bağlantısı: {base_url}/{observation_form_link}',
};
const formatTemplate = (tpl, ctx) => {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => (k in ctx ? ctx[k] : ''));
};

const buildPreview = (day, themeId, templates, themeTitle) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const formId = day?.form_id || '<form>';
  const ctxCommon = {
    parent_name: 'Veli',
    class_name: '5A',
    theme_title: themeTitle || day?.title || 'Tema',
    overview_message: '',
    activity_title: day?.title || 'Başlık',
    activity_message: (day?.message || '').trim() || '',
    student_first_name: 'Öğrenci',
    base_url: baseUrl,
    observation_form_link: `${themeId ? `value-evaluation/${themeId}/${formId}` : 'value-evaluation/<tema>/<form>'}`,
  };

  if (day?.type === 'overview') {
    return formatTemplate(templates.overview || defaultTemplates.overview, {
      ...ctxCommon,
      theme_title: ctxCommon.theme_title || 'Tema',
      overview_message: (day?.message || '').trim() || '',
    });
  }
  if (day?.type === 'activity') {
    return formatTemplate(templates.activity || defaultTemplates.activity, {
      ...ctxCommon,
      activity_title: ctxCommon.activity_title,
      activity_message: ctxCommon.activity_message || '(Mesaj ekleyin)',
    });
  }
  if (day?.type === 'observation') {
    return formatTemplate(templates.observation || defaultTemplates.observation, {
      ...ctxCommon,
      observation_form_link: ctxCommon.observation_form_link,
      student_first_name: ctxCommon.student_first_name,
    });
  }
  return `${day?.title || ''}\n${day?.message || ''}`;
};

const toHtmlPreview = (text) => {
  const escaped = (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const withBullets = escaped.replace(/(^|\n)-\s+/g, '$1• ');
  return withBullets.replace(/\*(.+?)\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />');
};

const ValuesThemeManager = ({ schoolId, onError, onSuccess }) => {
  const [themes, setThemes] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const [editor, setEditor] = useState(defaultTheme());
  const [templates, setTemplates] = useState(defaultTemplates);
  const [expandedDayId, setExpandedDayId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const dayIndexNext = useMemo(() => {
    if (!editor?.days?.length) return 1;
    return Math.max(...editor.days.map((d) => Number(d.index) || 0)) + 1;
  }, [editor?.days]);

  const loadThemes = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await listThemes(schoolId);
      setThemes(data.items || []);
    } catch (e) {
      onError?.('Temalar yüklenemedi.');
    } finally {
      setLoadingList(false);
    }
  }, [schoolId, onError]);

  const loadTheme = async (themeId) => {
    setDetailLoading(true);
    try {
      const data = await getTheme(schoolId, themeId);
      const days = normalizeDayIndices(
        (data.activities || []).map((a, idx) => ({
          client_id: newClientId(),
          index: Number(a.sort_order) || idx + 1,
          title: a.title || '',
          message: a.message || '',
          type: a.activity_type || 'activity',
          form_id: a.form_id ?? null,
        })),
      );
      const formDef = (data.forms && data.forms.length ? data.forms[0].definition : null) || defaultForm();
      const normalized = {
        ...defaultTheme(),
        id: data.id,
        title: data.title || '',
        overview: data.overview || '',
        days,
        form: formDef,
      };
      setSelectedThemeId(Number(data.id) || themeId);
      setEditor(normalized);
      setExpandedDayId(null);
    } catch (e) {
      onError?.('Tema detayları yüklenemedi.');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getValueTemplates();
        setTemplates({
          overview: data.overview || defaultTemplates.overview,
          activity: data.activity || defaultTemplates.activity,
          observation: data.observation || defaultTemplates.observation,
        });
      } catch (_) {
        setTemplates(defaultTemplates);
      }
    })();
  }, []);

  const handleNew = () => {
    setSelectedThemeId(null);
    setEditor(defaultTheme());
    setExpandedDayId(null);
  };

  const updateDay = (idx, patch) => {
    setEditor((prev) => {
      const days = prev.days.map((d, i) => (i === idx ? { ...d, ...patch } : d));
      return { ...prev, days };
    });
  };

  const addDay = () => {
    setEditor((prev) => ({
      ...prev,
      days: [
        ...prev.days,
        { client_id: newClientId(), index: dayIndexNext, title: `${dayIndexNext}. Gün`, message: '', type: 'activity', form_id: null },
      ],
    }));
  };

  const removeDay = (idx) => {
    setEditor((prev) => ({ ...prev, days: normalizeDayIndices(prev.days.filter((_, i) => i !== idx)) }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setEditor((prev) => {
      const oldIndex = (prev.days || []).findIndex((d) => d.client_id === active.id);
      const newIndex = (prev.days || []).findIndex((d) => d.client_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const moved = arrayMove(prev.days || [], oldIndex, newIndex);
      return { ...prev, days: normalizeDayIndices(moved) };
    });
  };

  const updateFormGroup = (gIdx, patch) => {
    setEditor((prev) => {
      const groups = (prev.form?.groups || []).map((g, i) => (i === gIdx ? { ...g, ...patch } : g));
      return { ...prev, form: { ...(prev.form || defaultForm()), groups } };
    });
  };

  const addGroup = () => {
    setEditor((prev) => {
      const form = prev.form || defaultForm();
      const nextIndex = (form.groups?.length || 0) + 1;
      const nextGroup = {
        id: `g${nextIndex}`,
        title: `Grup ${nextIndex}`,
        emoji: '',
        questions: [{ id: `q${nextIndex}_1`, text: '' }],
      };
      return { ...prev, form: { ...form, groups: [...(form.groups || []), nextGroup] } };
    });
  };

  const removeGroup = (gIdx) => {
    setEditor((prev) => {
      const groups = (prev.form?.groups || []).filter((_, i) => i !== gIdx);
      return { ...prev, form: { ...(prev.form || defaultForm()), groups } };
    });
  };

  const updateQuestion = (gIdx, qIdx, patch) => {
    setEditor((prev) => {
      const form = prev.form || defaultForm();
      const groups = (form.groups || []).map((g, gi) => {
        if (gi !== gIdx) return g;
        const questions = (g.questions || []).map((q, qi) => (qi === qIdx ? { ...q, ...patch } : q));
        return { ...g, questions };
      });
      return { ...prev, form: { ...form, groups } };
    });
  };

  const addQuestion = (gIdx) => {
    setEditor((prev) => {
      const form = prev.form || defaultForm();
      const groups = (form.groups || []).map((g, gi) => {
        if (gi !== gIdx) return g;
        const nextIdx = (g.questions?.length || 0) + 1;
        return { ...g, questions: [...(g.questions || []), { id: `${g.id}_q${nextIdx}`, text: '' }] };
      });
      return { ...prev, form: { ...form, groups } };
    });
  };

  const removeQuestion = (gIdx, qIdx) => {
    setEditor((prev) => {
      const form = prev.form || defaultForm();
      const groups = (form.groups || []).map((g, gi) => {
        if (gi !== gIdx) return g;
        return { ...g, questions: (g.questions || []).filter((_, qi) => qi !== qIdx) };
      });
      return { ...prev, form: { ...form, groups } };
    });
  };

  const normalizePayload = () => {
    const title = (editor.title || '').trim();
    const activities = (editor.days || []).map((d, idx) => ({
      sort_order: Number(d.index) || idx + 1,
      title: d.title || `Gün ${idx + 1}`,
      message: d.message || '',
      activity_type: d.type || 'activity',
      ...(d.type === 'observation' ? { form_id: d.form_id || null } : {}),
    }));

    return {
      ...(selectedThemeId ? { id: Number(selectedThemeId) } : {}),
      title,
      overview: editor.overview || '',
      is_active: true,
      form: editor.form || defaultForm(),
      activities,
    };
  };

  const handleSave = async () => {
    const payload = normalizePayload();
    if (!payload.title) {
      onError?.('Başlık zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const res = await saveTheme(schoolId, payload, !!selectedThemeId);
      onSuccess?.('Tema kaydedildi.');
      const themeId = res?.theme?.id || payload.id;
      setSelectedThemeId(themeId);
      await loadThemes();
      if (themeId) await loadTheme(themeId);
    } catch (e) {
      onError?.('Tema kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedThemeId) return;
    setSaving(true);
    try {
      await deleteTheme(schoolId, selectedThemeId);
      onSuccess?.('Tema silindi.');
      setSelectedThemeId(null);
      setEditor(defaultTheme());
      await loadThemes();
    } catch (e) {
      onError?.('Tema silinemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ColorLensIcon fontSize="small" /> Temalar
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={loadThemes} disabled={loadingList}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleNew}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            {loadingList &&
              [0, 1, 2].map((k) => (
                <Box
                  key={`sk-${k}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ width: '70%', height: 18, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ width: '40%', height: 14, bgcolor: 'grey.200', borderRadius: 1 }} />
                </Box>
              ))}
            {themes.map((t) => {
              const selected = Number(selectedThemeId) === Number(t.id);
              return (
                <Paper
                  key={t.id}
                  variant={selected ? 'outlined' : 'elevation'}
                  sx={{
                    p: 1.5,
                    borderColor: selected ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                    bgcolor: selected ? 'primary.lighter' : 'background.paper',
                    boxShadow: selected ? 3 : 0,
                    borderLeft: selected ? '4px solid' : '1px solid',
                    borderLeftColor: selected ? 'primary.main' : 'divider',
                    '&:hover': { bgcolor: selected ? 'primary.lighter' : 'grey.50' },
                    transition: 'all 0.15s ease-in-out',
                  }}
                  aria-selected={selected}
                  onClick={() => loadTheme(t.id)}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: selected ? 'primary.main' : 'text.primary' }}>
                    {t.title || `Tema ${t.id}`}
                  </Typography>
                  <Typography variant="body2" color={selected ? 'primary.main' : 'text.secondary'}>
                    ID: {t.id}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${t.activity_count || 0} öğe`} />
                    <Chip size="small" label={`${t.form_count || 0} form`} variant={t.form_count ? 'filled' : 'outlined'} />
                  </Stack>
                </Paper>
              );
            })}
            {themes.length === 0 && !loadingList && (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Henüz tema yok.
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleNew}>
                  Yeni Tema Ekle
                </Button>
              </Box>
            )}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Tema Düzenleyici</Typography>
            <Stack direction="row" spacing={1}>
              {selectedThemeId && (
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Sil
                </Button>
              )}
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || detailLoading}>
                Kaydet
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tema ID"
                value={editor.id || ''}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Başlık"
                value={editor.title}
                onChange={(e) => setEditor((prev) => ({ ...prev, title: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Genel Bakış"
                value={editor.overview}
                onChange={(e) => setEditor((prev) => ({ ...prev, overview: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: '#d0c7bd',
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: '#EFEAE2',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#111B21' }}>
                  WhatsApp Önizleme (Genel Bakış)
                </Typography>
                <Box
                  sx={{
                    fontSize: 14,
                    color: '#111B21',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-line',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: toHtmlPreview(
                      formatTemplate(templates.overview || defaultTemplates.overview, {
                        parent_name: 'Veli',
                        class_name: '5A',
                        theme_title: editor.title || 'Tema',
                        overview_message: editor.overview || '',
                      }),
                    ),
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ArticleIcon fontSize="small" />
            <Typography variant="subtitle1">İçerik Öğeleri</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addDay}>
              Gün Ekle
            </Button>
          </Box>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={(editor.days || []).map((d) => d.client_id)} strategy={verticalListSortingStrategy}>
              <Stack spacing={2.5}>
                {(editor.days || []).map((day, idx) => (
                  <SortableDayAccordion
                    key={day.client_id}
                    day={day}
                    idx={idx}
                    expanded={expandedDayId === day.client_id}
                    onToggle={() => setExpandedDayId(expandedDayId === day.client_id ? null : day.client_id)}
                    dayTypes={dayTypes}
                    updateDay={updateDay}
                    removeDay={removeDay}
                    templates={templates}
                    editor={editor}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1">Gözlem Formu</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Form ID"
                value={editor.form?.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                disabled
                helperText="Otomatik form kimliği"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Açıklama"
                value={editor.form?.description || ''}
                onChange={(e) => setEditor((prev) => ({ ...prev, form: { ...(prev.form || defaultForm()), description: e.target.value } }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Notlar Etiketi"
                value={editor.form?.notes_label || ''}
                onChange={(e) => setEditor((prev) => ({ ...prev, form: { ...(prev.form || defaultForm()), notes_label: e.target.value } }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Güçlü Yönler Etiketi"
                value={editor.form?.strengths_label || ''}
                onChange={(e) => setEditor((prev) => ({ ...prev, form: { ...(prev.form || defaultForm()), strengths_label: e.target.value } }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Gelişmesi Gerekenler"
                value={editor.form?.improvements_label || ''}
                onChange={(e) => setEditor((prev) => ({ ...prev, form: { ...(prev.form || defaultForm()), improvements_label: e.target.value } }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Genel Yorum"
                value={editor.form?.general_label || ''}
                onChange={(e) => setEditor((prev) => ({ ...prev, form: { ...(prev.form || defaultForm()), general_label: e.target.value } }))}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
            <Typography variant="subtitle2">Form Grupları</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addGroup}>
              Grup Ekle
            </Button>
          </Box>
          {editor.form && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
                bgcolor: 'linear-gradient(145deg, rgba(30,41,59,0.12), rgba(15,23,42,0.2))',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Gözlem Formu Önizleme
              </Typography>
              <Box
                sx={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  p: 2,
                  background: 'linear-gradient(145deg, rgba(30,41,59,0.18), rgba(12,18,38,0.18))',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111B21', mb: 0.5 }}>
                  {editor.form.title || editor.title || 'Form Başlığı'}
                </Typography>
                {editor.form.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {editor.form.description}
                  </Typography>
                )}
                <Stack spacing={1.5}>
                  {(editor.form.groups || []).map((g) => (
                    <Box
                      key={g.id}
                      sx={{
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 1.5,
                        p: 1.5,
                        backgroundColor: '#f8fafc',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: 20 }}>{g.emoji || '⭐️'}</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {g.title || g.id}
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {(g.questions || []).map((q) => (
                          <Box
                            key={q.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              backgroundColor: '#fff',
                              border: '1px solid rgba(0,0,0,0.05)',
                              borderRadius: 1.5,
                              p: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{q.text}</Typography>
                            <Stack direction="row" spacing={1}>
                              <Chip label="Evet" size="small" color="success" variant="outlined" />
                              <Chip label="Hayır" size="small" color="error" variant="outlined" />
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
          <Stack spacing={2}>
            {(editor.form?.groups || []).map((group, gIdx) => (
              <Accordion
                key={gIdx}
                sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 1 }}
                defaultExpanded={gIdx === 0}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip size="small" label={group.id || `g${gIdx + 1}`} />
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>{group.title || 'Grup Başlığı'}</Typography>
                    <Button
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeGroup(gIdx);
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      Sil
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Grup ID"
                        value={group.id}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        disabled
                        helperText="Otomatik"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Başlık"
                        value={group.title}
                        onChange={(e) => updateFormGroup(gIdx, { title: e.target.value })}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Sorular</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addQuestion(gIdx)}>
                          Soru Ekle
                        </Button>
                      </Stack>
                      <Stack spacing={1}>
                        {(group.questions || []).map((q, qIdx) => (
                          <Stack key={qIdx} direction="row" spacing={1} alignItems="center">
                            <TextField
                              label="Soru ID"
                              value={q.id}
                              sx={{ width: 160 }}
                              InputProps={{ readOnly: true }}
                              disabled
                              helperText="Otomatik"
                            />
                            <TextField
                              label="Soru Metni"
                              value={q.text}
                              onChange={(e) => updateQuestion(gIdx, qIdx, { text: e.target.value })}
                              fullWidth
                            />
                            <IconButton color="error" onClick={() => removeQuestion(gIdx, qIdx)} aria-label="Soruyu sil">
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
            {(editor.form?.groups || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Formda henüz soru yok. Grup ekleyin.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ValuesThemeManager;
